import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useStoreContext } from '../../../store/context';
import { Grid } from '../../../shared/grid';
import './Tables.scss';

import Box from '@mui/material/Box';
import { Autocomplete, TextField, Button } from '@mui/material';
import { ValidCharacters, errorResponse, validateCharacters } from '../../../store/common'; 
import { FuelSampleTable } from './FuelSampleTable';
import { useAddAlert } from 'src/store/actions';
import { SiteInfo } from 'src/types';
import { getCodes, getHeader, getSiteKeysForSearch } from 'src/apis/getFuelSampleCalls';
import { alertMessages } from 'src/constants';
import moment from 'moment';

export const transformIds = (data:any) =>{
	const stationsIds:string[] =[];
	data.map((station:any)=>{
		stationsIds.push(station.station_id.toString());
		return station;
	});
	return stationsIds;
};

let isShowTableData = false;

/***
 * This is the SiteTable Page
 * The route will have to be fixed from when a user clicks on it from
 * the pop up station route and coming from the header.
 * 
 */
export const SiteTable: React.FC = () => {
  const allCodeObj =  useMemo(()=>({code_key: "All",code_table_id: -1, code_value:'All', type:'All'}),[]);
   
	const [state, dispatch] = useStoreContext();
  const [inputSiteSelection, setInputSiteSelection] = useState('');
  const [sampleType, setSampleType] = useState('');
  const [isViewTableButtonDisabled, setIsViewTableButtonDisabled] = useState(true);
  const [openSiteMetaData, setOpenSiteMetaData] = useState(false);
  const [showRequiredFieldsLegend, setShowRequiredFieldsLegend] = useState(true);
  const [sampleSubCats, setSampleSubCats] = useState<any[]>([allCodeObj]);
  const [methods, setMethods] = useState<any[]>([allCodeObj]);
  const [sites, setSites] = useState<any[]>([]);
  const [sampleTypes, setSampleTypes] = useState<any[]>([allCodeObj]);
  const [siteInfo, setSiteInfo] = useState<SiteInfo>();
  const [isContentGrayedOut, setIsContentGrayedOut] = useState(true);

  const [startDate, setStartDate] = useState<any>(new Date(new Date().getFullYear(), 0, 1));
  const [endDate, setEndDate] = useState<any>(moment().utc().toDate());


  const addAlert = useAddAlert(dispatch);
  
  let fuelSampleProps = {      
    siteInfo: siteInfo,
    sampleType: sampleType,
    isShowTableData: isShowTableData,
    openSiteMetaData: openSiteMetaData,
    setOpenMetaData: setOpenSiteMetaData,
    isContentGrayedOut: isContentGrayedOut,
    showRequiredFieldsLegend: showRequiredFieldsLegend,
    sampleSubCats: sampleSubCats,
    methods: methods,
    startDate: startDate,
    endDate: endDate,
    setEndDate: setEndDate,
    setStartDate: setStartDate,
  };

  function showDataTable(isShowFlag: boolean){
    isShowTableData = isShowFlag; 
    setIsContentGrayedOut(!isShowFlag);
  }

  /* 
    UseEffect on initial load to get dropdown options
  */
  useEffect(()=>{
    if(sites && sites.length<1){
      (async () =>{
        try{
          const waitResponse:any = await getSiteKeysForSearch(null, addAlert);
          setSites(waitResponse);
        }
        catch(error){
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        }
      })();
    }
  },[addAlert, sites]);

  const dynamicTypesWithDates = useCallback((site:any)=>{
    const requestVars = {
      startDate: startDate, 
      endDate: endDate, 
      id: site?.site_id, 
      status: 'All',
      category: 'All',
      subCategory: 'All',
      method: 'All',
      type: 'All'
    };
    (async()=>{
      try{
        const headersPromise:any = await getHeader(null, addAlert, requestVars);
        if (headersPromise.length > 0) {
          let unique:any[] = [...new Set(headersPromise.map((item:any) => item.fuel_type))];
          unique.sort(function(a, b) {
            return a.localeCompare(b);
         });
          unique.unshift(allCodeObj.code_key);
          setSampleTypes(unique)
        }
        else {
          setSampleTypes([allCodeObj.code_key])
        }
      }
      catch(error){
        errorResponse(error, addAlert, alertMessages.siteSampleData);
      } 
    })();
  },[addAlert,allCodeObj.code_key, endDate, startDate]);

  useEffect(()=>{
    if(sampleSubCats && sampleSubCats.length<2){
      (async()=>{
        try{
          const requestVars ={
            returnAll: true,
            type: null
          }
          const codeResponse:any = await getCodes(requestVars);
          const types = codeResponse.types;
          const cats = codeResponse.cats;
          cats.unshift(allCodeObj);
          const statuses = codeResponse.statuses;
          statuses.unshift(allCodeObj)
          const subCats = codeResponse.subCats;
          subCats.unshift(allCodeObj)
          const methods = codeResponse.methods;
          methods.unshift(allCodeObj)
          setSampleTypes(types.sort((a:any,b:any)=>a.code_key.localeCompare(b.code_key)));
          setSampleSubCats(subCats.sort((a:any,b:any)=>a.code_key.localeCompare(b.code_key)));
          setMethods(methods.sort((a:any,b:any)=>a.code_key.localeCompare(b.code_key)));
        }
        catch(error){
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        } 
      })();
    }
  },[sampleSubCats, addAlert, allCodeObj]);

  /**
   * handleColorLegend is to iterature through current element's legend colors
   * then it will compare it to the values within that LegendColor
   * and return a color so the background is colored accordingly
  */
  useEffect(() => { 
    const currentSelectedSite = sites.find(site => site.site_key === inputSiteSelection)
    dynamicTypesWithDates(currentSelectedSite)
  }, [state.colorLegend, state.table, inputSiteSelection, dynamicTypesWithDates, sites])

  // Define a state variable to track whether to gray out content


  const handleSampleTypeInputField = (newInputValue : any) => {
    if (newInputValue === '') {
      showDataTable(false);
      setIsViewTableButtonDisabled(true);
    } else{
      setIsViewTableButtonDisabled(false);
    }  
  };

  const handleViewTableButton = () => {
    setShowRequiredFieldsLegend(false);
    showDataTable(true);
    setSiteInfo(() => {
      return sites.find(site => site.site_key === inputSiteSelection);
    });
    fuelSampleProps = {
      siteInfo: siteInfo,
      sampleType: sampleType,
      isShowTableData: isShowTableData,
      openSiteMetaData: openSiteMetaData,
      setOpenMetaData: setOpenSiteMetaData,
      isContentGrayedOut: isContentGrayedOut,
      showRequiredFieldsLegend: showRequiredFieldsLegend,
      sampleSubCats: sampleSubCats,
      methods: methods,
      startDate: startDate,
      endDate: endDate,
      setStartDate: setStartDate,
      setEndDate: setEndDate,

    }
  };

  return (
    <div style={{padding: '10px', marginLeft: '20px'}}>

      {/* LABELS */}
      <Grid row gap="md" className="first-section">
        <Grid desktop={{ col: 3 }} className="font-sans-md text-bold">
          <label htmlFor="input-field-site" className="text-bold" style={{ fontSize: '18px' }}>Site*</label>
        </Grid>
        <Grid desktop={{ col: 3}} className="font-sans-md text-bold">
          <label htmlFor="input-field-sample-type" className="text-bold" style={{ fontSize: '18px' }}>Sample Type*</label>
        </Grid>
        <Grid></Grid>
      </Grid>

      {/* INPUT FIELDS */}
      <Grid row gap="md" className="margin-top-1">
        {/* INPUT FIELD: Site*/}
        <Grid desktop={{ col: 3 }}>
          <div className='searchSites' style={{ padding: '0px' }}>
            <i className="fas fa-search" />
            <Autocomplete
              freeSolo // <== the user input is not bound to provided options else 'Warning The value provided to Autocomplete is invalid'
              className='height-55'
              onChange={(_event: any, newValue: any) => {
                setIsViewTableButtonDisabled(true);
                setInputSiteSelection(newValue.site_key);
                setSampleType('');
                setOpenSiteMetaData(false);
                setShowRequiredFieldsLegend(true);
                dynamicTypesWithDates(newValue)
              }}
              id="input-field-site"
              filterOptions={(options, state)=>{
                const inputValue = state.inputValue.trim().toLowerCase().split(/[ -]+/);
                const filtered = options.filter((option)=>{
                  const optionWords = option.site_key.toLowerCase().split(/[ -]+/)

                  return inputValue.every((word)=>{
                    return optionWords.some((optionWord)=> optionWord.startsWith(word))
                  })
                })
                return filtered
              }}

              options={sites.map((option, index) => ({
                ...option,
                uniqueKey: `${option.site_key}-${index}`
              }))}
              getOptionLabel={(option)=>option.display_key}
              renderOption={(props, option) => (
                <li {...props} key={option.uniqueKey}>
                  {option.display_key}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  placeholder='Search all sites...'
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField, ValidCharacters.Period)}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </div>
        </Grid>

        {/* INPUT FIELD: Sample Type */}
        <Grid desktop={{ col: 3 }}>
          <div className='searchSites' style={{ padding: '0px' }}>
            <i className="fas fa-search" />
            <Autocomplete
              freeSolo // <== the user input is not bound to provided options else 'Warning The value provided to Autocomplete is invalid'
              aria-disabled={(inputSiteSelection.trim() === '')}
              className='height-55'
              value={sampleType}
              onChange={(_event: any, newValue: any) => {
                setSampleType(newValue);
                setIsViewTableButtonDisabled(false);
                setOpenSiteMetaData(false);
                setShowRequiredFieldsLegend(true);
              }}
              inputValue={sampleType}
              onInputChange={(_event, newInputValue) => {
                setSampleType(newInputValue);
                handleSampleTypeInputField(newInputValue);
                setOpenSiteMetaData(false);
                setShowRequiredFieldsLegend(true);
                showDataTable(false);
              }}
              disableClearable
              id="input-field-sample-type"
              options={sampleTypes}
              renderInput={(params) => (
                <TextField
                  placeholder='Search all types...'
                  value={sampleType}
                  onInput={(e) => validateCharacters(e, ValidCharacters.TextField, ValidCharacters.Period, ValidCharacters.PercentSign)}
                  data-testid={'sample-type-search'}
                  {...params}
                  InputProps={{
                    ...params.InputProps,
                    type: 'search',
                  }}
                />
              )}
            />
          </div>
        </Grid>

        {/* BUTTON "VIEW TABLE 2" */}

        <Grid desktop={{ col: 2 }} style={{ display: 'inline-flex' }}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100"
          >
            <Button className={'height-55'} onClick={handleViewTableButton} style={{ height: '50px', width: '150px' }} variant="contained" type="submit" disabled={isViewTableButtonDisabled}>
              View Table
            </Button>
          </Box>
        </Grid>
      </Grid>
      <FuelSampleTable fuelSampleProps={fuelSampleProps}/>

    </div>
  );
};